import {Component, FontAwesomeIcon} from 'substance'
import {AddToBylineComponent} from './AddToByline'
import {NilUUID} from '../../../lib/utils/NilUUID'

/**
 * @typedef UIByline.props
 * @property {Node} node - The substance node, which requires an "authors"-property
 * @property {boolean} bylineSearch - If search should be enabled
 * @property {string} isolatedNodeState - The isolatedNodeState property from the parent Component
 */

/**
 * An Author Byline Component
 *
 * @name UIByline
 * @class
 *
 * @example
 * import {UIByline} from 'writer'
 *
 * render($$) {
 *     const el = $$('div')
 *
 *     return el.append(
 *         $$(UIByline, {
 *             node: this.props.node,
 *             bylineSearch: true,
 *             isolatedNodeState: this.props.isolatedNodeState
 *         })
 *     )
 * }
 *
 * @param {UIByline.props} props
 * @property {UIByline.props} props
 */
class BylineComponent extends Component {

    render($$) {
        const el = $$('div')
        const authorList = $$('ul')
            .addClass('byline-image-authorlist')
            .attr('contenteditable', false)

        this.props.node.authors.forEach((item) => {
            const authorItem = this._renderAuthor($$, item)
            if (authorItem) {
                authorList.append(authorItem)
            }
        })

        if (['selected', 'focused'].includes(this.props.isolatedNodeState)) {
            authorList.append($$('a')
                .addClass('add-author-link')
                .on('click', this._openAddToByline)
                .append(`+ ${this.getLabel('Add to image byline')}`))
        }

        el.append($$('div')
            .attr('contenteditable', false)
            .addClass('x-im-image-authors')
            .append(authorList)
        )

        return el
    }

    removeAuthor(author) {
        const refId = (NilUUID.isNilUUID(author.uuid)) ? author.name : author.uuid
        const authors = this.props.node.authors

        delete this.refs[`item-${refId}`]

        for(let n = 0; n < authors.length; n++) {
            if (!NilUUID.isNilUUID(author.uuid) && authors[n].uuid === author.uuid) {
                authors.splice(n, 1)
                break
            }
            else if (NilUUID.isNilUUID(author.uuid) && authors[n].name === author.name) {
                authors.splice(n, 1)
                break
            }
        }

        this.props.node.setAuthors(authors)
    }

    _openAddToByline() {
        this.context.api.ui.showDialog(
            AddToBylineComponent,
            {
                node: this.props.node,
                bylineSearch: this.props.bylineSearch,
                addAuthor: (author) => {
                    this.props.node.addAuthor(author)
                }
            },
            {
                title: this.getLabel('Add to image byline'),
                global: true,
                primary: this.getLabel('Close'),
                secondary: false,
                focusPrimary: false
            }
        )
    }

    /**
     * @param $$
     * @param author
     *
     * @private
     *
     * @returns {VirtualElement}
     */
    _renderAuthor($$, author) {
        const refId = (NilUUID.isNilUUID(author.uuid)) ? author.name : author.uuid
        const avatarEl = this._renderAvatar($$, author, refId)

        return $$('li').append(
            $$('div').addClass('author-element').append([
                avatarEl,
                $$('div').append([
                    $$('strong').append(author.name)
                ]),
                $$('span').append(
                    $$('a').append(
                        $$(FontAwesomeIcon, {icon: 'fa-times-circle'})
                    )
                        .attr('title', this.getLabel('Remove'))
                        .on('click', () => {
                            this.removeAuthor(author)
                        })
                )
            ]).ref(`container-${refId}`)
        ).ref(`item-${refId}`)

    }

    /**
     * @param $$
     * @param author
     * @param refId
     *
     * @private
     *
     * @returns {*}
     */
    _renderAvatar($$, author, refId) {
        const Avatar = this.context.configurator.getComponent('UIAvatar')
        const source = {
            avatarSource: 'gravatar',
            avatarId: author.email
        }

        return $$(Avatar, source).ref(`avatar-${refId}`)
    }
}

export {BylineComponent}
